import { apiRequest, getStoredToken, jsonBody } from './api';

interface UploadTicket {
  mode: 'presigned' | 'direct';
  key: string;
  uploadUrl: string;
  publicUrl: string;
  method: 'PUT';
  headers: Record<string, string>;
}

const MAX_UPLOAD_BYTES = 3 * 1024 * 1024;
const MAX_SOURCE_BYTES = 20 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/avif'];

function canvasBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

async function optimizeImage(file: File, kind: 'logo' | 'product'): Promise<File> {
  if (!ALLOWED_TYPES.includes(file.type)) throw new Error('Usa una imagen JPG, PNG, WebP o AVIF.');
  if (file.size > MAX_SOURCE_BYTES) throw new Error('La imagen de origen supera 20 MB.');

  try {
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    const maxDimension = kind === 'logo' ? 1200 : 1920;
    const scale = Math.min(1, maxDimension / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    const context = canvas.getContext('2d', { alpha: true });
    if (!context) throw new Error('No se pudo preparar la imagen.');
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();

    let blob: Blob | null = null;
    for (const quality of [0.86, 0.78, 0.68, 0.58]) {
      blob = await canvasBlob(canvas, 'image/webp', quality);
      if (blob && blob.size <= MAX_UPLOAD_BYTES) break;
    }
    if (!blob || blob.size > MAX_UPLOAD_BYTES) throw new Error('No se pudo optimizar la imagen por debajo de 3 MB.');
    const baseName = file.name.replace(/\.[^.]+$/, '').replace(/[^a-zA-Z0-9_-]+/g, '-').slice(0, 80) || kind;
    return new File([blob], `${baseName}.webp`, { type: 'image/webp', lastModified: Date.now() });
  } catch (error) {
    if (file.size <= MAX_UPLOAD_BYTES) return file;
    throw error instanceof Error ? error : new Error('No se pudo optimizar la imagen.');
  }
}

export async function uploadImage(source: File, kind: 'logo' | 'product'): Promise<{ key: string; publicUrl: string }> {
  const file = await optimizeImage(source, kind);
  if (file.size > MAX_UPLOAD_BYTES) throw new Error('La imagen supera el límite de 3 MB.');

  const ticket = await apiRequest<UploadTicket>('/api/uploads/presign', {
    method: 'POST',
    body: jsonBody({ kind, contentType: file.type, size: file.size, originalName: file.name }),
  });
  const headers = new Headers(ticket.headers);
  if (ticket.mode === 'direct') {
    const token = getStoredToken();
    if (token) headers.set('Authorization', `Bearer ${token}`);
  }
  const response = await fetch(ticket.uploadUrl, { method: 'PUT', headers, body: file });
  if (!response.ok) {
    const payload = await response.json().catch(() => null) as { error?: { message?: string } } | null;
    throw new Error(payload?.error?.message || 'No se pudo subir la imagen.');
  }
  await apiRequest('/api/uploads/complete', {
    method: 'POST',
    body: jsonBody({ key: ticket.key, contentType: file.type, size: file.size }),
  });
  return { key: ticket.key, publicUrl: ticket.publicUrl };
}
